import { useEffect, useMemo, useState } from 'react';
import {
  STopSellingItems,
  SDemographics,
  useDashboardProps,
  SBills,
  SHeaderData,
  SExpiryItem,
  SExpiredItem,
  SLowStockItem,
  SFootfall,
  SModalData,
  SCustomerCountData,
} from './types';
import { useAppContext } from 'contexts/AppContext';
import useApiStatusCheck from 'hooks/useAPIStatusCheck';
import { ApiError } from 'services/api/base';
import { DashboardService } from 'services/api/endpoints/DashboardService';
import { PieChartData } from './components/PieChart/types';
import { RDashboardData } from 'services/api/types/dashboard.types';
import { daysOrder, INITIAL_HEADER_DATA, INITIAL_MODAL_DATA, INITIAL_PIE_DATA } from './constants';
import { formatTimeRange, timeTo24Hour } from './utils';

const useDashboard = ({ from, to }: useDashboardProps) => {
  const [headerData, setHeaderData] = useState<SHeaderData>(INITIAL_HEADER_DATA);
  const [topSellingSku, setTopSellingSku] = useState<STopSellingItems[]>([]);
  const [expiryItem, setExpiryItem] = useState<SExpiryItem[]>([]);
  const [expiredItem, setExpiredItem] = useState<SExpiredItem[]>([]);
  const [lowStockItem, setLowStockItem] = useState<SLowStockItem[]>([]);
  const [demographics, setDemographics] = useState<SDemographics[]>([]);
  const [footfall, setFootfall] = useState<SFootfall[]>([]);
  const [bills, setBills] = useState<SBills[]>([]);
  const [customerCountData, setCustomerCountData] = useState<SCustomerCountData>(
    {} as SCustomerCountData,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<SModalData>(INITIAL_MODAL_DATA);

  const { snack } = useAppContext();
  const { startCheck, isPolling } = useApiStatusCheck('none');

  const handleShowMore = ({ title, columns, data, redirectUrl }: SModalData) => {
    setModalData({
      title,
      columns,
      data,
      redirectUrl,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => setModalOpen(false);

  // For Setting Header Data
  const generateDashboardData = (data: RDashboardData) => {
    const { liveFootfallCount, skuCount, outOfStockCount, itemCount } = data.headers;
    setHeaderData((prev) => ({
      ...prev,
      liveFootfallCount,
      skuCount,
      outOfStockCount,
      itemCount,
    }));

    setCustomerCountData({
      newCustomerCount: data.customerNewCount,
      repeatCustomerCount: data.customerRepeatCount,
    });

    setTopSellingSku(data.top_selling_items);
    setDemographics(data.demographics);
    setLowStockItem(data.low_stock_dashboard);
    setExpiredItem(data.expired_dashboard);
    setExpiryItem(data.near_expiry_dashboard);
    setBills(data.bills);
    setFootfall(data.footfall);
  };

  // For Calculating Sales Related Data
  const { staffMetrics, storeSales, deliverySales } = useMemo(() => {
    const dataMap = new Map<string, { sales: number; invoiceValue: number }>();

    let storeSales = 0;
    let deliverySales = 0;

    bills.forEach(({ billerName, netAmount, saleType }) => {
      const currentData = dataMap.get(billerName) || { sales: 0, invoiceValue: 0 };
      currentData.sales++;
      currentData.invoiceValue += netAmount;
      dataMap.set(billerName, currentData);

      saleType === 'Sale' ? storeSales++ : deliverySales++;
    });

    const staffMetrics = Array.from(dataMap.entries()).map(([name, { sales, invoiceValue }]) => ({
      name,
      sales,
      invoiceValue: invoiceValue.toFixed(2),
    }));

    return { staffMetrics, storeSales, deliverySales };
  }, [bills]);

  // For Generating Pie Data
  const generatePieData = (
    type: 'age' | 'sales' | 'customer' | 'category' | 'topSku',
  ): PieChartData => {
    const dataMap: Record<string, number> = {};
    const { repeatCustomerCount, newCustomerCount } = customerCountData;

    if (type === 'age') {
      demographics.forEach(({ age }) => {
        const category =
          age <= 25
            ? '0-25'
            : age <= 35
            ? '26-35'
            : age <= 45
            ? '36-45'
            : age <= 60
            ? '46-60'
            : '60+';
        dataMap[category] = (+dataMap[category] || 0) + 1;
      });
    } else if (type === 'category') {
      demographics.forEach(({ ageCategory }) => {
        dataMap[ageCategory] = (+dataMap[ageCategory] || 0) + 1;
      });
    } else if (type === 'sales') {
      dataMap['Store Sales'] = storeSales;
      dataMap['Delivery Sales'] = deliverySales;
    } else if (type === 'topSku') {
      topSellingSku.slice(0, 5).forEach(({ name, saleQty }) => {
        let qty = parseFloat(saleQty);
        if (saleQty.includes(':')) {
          qty++;
        }
        if (!isNaN(qty)) {
          dataMap[name] = qty;
        }
      });
    } else {
      dataMap['Repeat Customer'] = repeatCustomerCount;
      dataMap['New Customer'] = newCustomerCount;
    }

    if (type === 'customer' && repeatCustomerCount == 0 && newCustomerCount == 0) {
      return INITIAL_PIE_DATA;
    }

    return {
      labels: Object.keys(dataMap),
      datasets: [
        {
          data: Object.values(dataMap),
          backgroundColor: ['#2562cd', '#224e99', '#173567', '#85a9d9', '#538cee'],
        },
      ],
    };
  };

  // For Generating Bar Chart Data
  const generateChartData = (type?: string) => {
    const dataMap = new Map<string, { footfall: number; sales: number }>();

    const updateDataMap = (timeRange: string, key: 'footfall' | 'sales', value: number) => {
      if (!dataMap.has(timeRange)) {
        dataMap.set(timeRange, { footfall: 0, sales: 0 });
      }
      const currentData = dataMap.get(timeRange);
      if (currentData) {
        currentData[key] += value;
      }
    };

    // Calculate Footfall
    footfall.forEach(({ createdOn, personCount }) => {
      const timeRange = formatTimeRange(createdOn, type);
      updateDataMap(timeRange, 'footfall', personCount);
    });

    // Calculate Sales
    bills.forEach(({ billedOn }) => {
      const timeRange = formatTimeRange(billedOn, type);
      updateDataMap(timeRange, 'sales', 1);
    });

    const labels = Array.from(dataMap.keys());

    // Sorting logic
    if (type === 'day') {
      // Sort by day order
      labels.sort((a, b) => daysOrder.indexOf(a) - daysOrder.indexOf(b));
    } else {
      // Sort AM first, then PM
      labels.sort((a, b) => timeTo24Hour(a) - timeTo24Hour(b));
    }

    return {
      labels,
      datasets: [
        {
          type: 'bar' as const,
          label: 'Footfall',
          data: labels.map((label) => dataMap.get(label)?.footfall ?? 0),
          backgroundColor: '#2562cd',
          borderRadius: 8, // Rounded corners
          barPercentage: 0.8, // Adjusts bar width
          barThickness: 50, // Set a fixed width (in pixels)
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'line' as const,
          label: 'Sales',
          data: labels.map((label) => dataMap.get(label)?.sales ?? 0),
          borderColor: '#85a9d9',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          yAxisID: 'y',
          order: 1,
        },
      ],
    };
  };

  // Fetch Dashboard Data
  const getDashboardData = async () => {
    try {
      const { data: responseData } = await DashboardService.get(from, to);
      if (responseData?.data.async) {
        startCheck(
          responseData.data.task,
          (callbackData) => generateDashboardData(callbackData as RDashboardData),
          3000,
        );
      }
    } catch (error) {
      snack.show((error as ApiError).userMessage, 'error');
    }
  };

  useEffect(() => {
    const { salesMargin, salesValue } = bills.reduce(
      (acc, { netAmount, marginPercent }) => {
        acc.salesMargin = acc.salesMargin + (netAmount * marginPercent) / 100;
        acc.salesValue = acc.salesValue + netAmount;
        return acc;
      },
      { salesValue: 0, salesMargin: 0 },
    );
    const maleCount = demographics.filter(({ gender }) => gender === 'male').length;
    const femaleCount = demographics.filter(({ gender }) => gender === 'female').length;

    const avgOrderValue = bills.length ? salesValue / bills.length : 0;
    const demographicCount = `${maleCount}M / ${femaleCount}F`;

    setHeaderData((prev) => ({
      ...prev,
      avgOrderValue,
      demographicCount,
      salesValue,
      salesMargin,
    }));
  }, [bills, demographics]);

  useEffect(() => {
    getDashboardData();
  }, [from, to]);

  return {
    headerData,
    topSellingSku,
    demographics,
    footfall,
    staffMetrics,
    storeSales,
    deliverySales,
    bills,
    expiredItem,
    expiryItem,
    lowStockItem,
    isPolling,
    modalOpen,
    modalData,
    handleCloseModal,
    handleShowMore,
    generatePieData,
    generateChartData,
  };
};

export default useDashboard;
